import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import axios from "axios";

import LoginPage      from "./components/LoginPage";
import ExtractionPage from "./components/ExtractionPage";
import ResultPage     from "./components/ResultPage";
import { clearAuth, getToken, isLoggedIn } from "./auth";

import "./index.css";

//export const API_BASE = "http://localhost:8081/coc-extractor/api/coc";
export const API_BASE = 'https://coc.betalearnings.com/coc-extractor/api/coc'

const THEME = {
  token: {
    colorPrimary: "#141414",
    borderRadius: 8,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

// Inject Bearer token into every request
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// ── ProtectedRoute ─────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  if (!isLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// ── 401 handler — inside BrowserRouter so useNavigate works ───────────────
function AxiosInterceptor() {
  const navigate = useNavigate();
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && !err.config?.url?.endsWith("/login")) {
          clearAuth();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);
  return null;
}

// ── Root App ───────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ConfigProvider theme={THEME}>
      {/*
        basename="/coc_extract" means ALL route paths below are relative
        to /coc_extract. Write paths WITHOUT /coc_extract prefix here —
        the browser URL will automatically include it.

        /login      → browser shows: /coc_extract/login
        /coc_ui     → browser shows: /coc_extract/coc_ui
        /coc_result → browser shows: /coc_extract/coc_result
      */}
      <BrowserRouter basename="/coc_extract">
        <AxiosInterceptor />
        <Routes>
          {/* Root /coc_extract → redirect based on auth */}
          <Route
            index
            element={
              isLoggedIn()
                ? <Navigate to="/coc_ui" replace />
                : <Navigate to="/login" replace />
            }
          />

          {/* Login */}
          <Route path="/login" element={<LoginPage />} />

          {/* Main extraction page */}
          <Route
            path="/coc_ui"
            element={
              <ProtectedRoute>
                <ExtractionPage />
              </ProtectedRoute>
            }
          />

          {/* Results page */}
          <Route
            path="/coc_result"
            element={
              <ProtectedRoute>
                <ResultPage />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route
            path="*"
            element={
              isLoggedIn()
                ? <Navigate to="/coc_ui" replace />
                : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}